import { createApi } from '@reduxjs/toolkit/query/react';
import {
  CreateUploadBodyPrivate,
  CreateUploadResponsePrivate,
  FileResponsePrivate,
  GetManyFilesQueryPrivate,
  GetManyFilesResponsePrivate,
} from '@tests/types';

import { ApiPath } from '@/constants';
import { authGuardQuery } from '@/services/api/queries';
import { createUrl } from '@/utils';

export interface UploadProps {
  onUploadProgress?(progressEvent: ProgressEvent): void;
  url: string;
}
export interface UploadFileProps extends UploadProps {
  contentType: string;
  data: ArrayBuffer;
}

export const filesApi = createApi({
  baseQuery: authGuardQuery,
  endpoints: (builder) => ({
    createUploadFile: builder.mutation<CreateUploadResponsePrivate, CreateUploadBodyPrivate>({
      query: (data) => ({
        data,
        method: 'POST',
        url: createUrl(ApiPath.Files, ApiPath.CreateUpload),
      }),
    }),
    deleteBackground: builder.mutation<any, { id: number; testId: number }>({
      query: ({ id, testId }) => ({
        data: {},
        method: 'DELETE',
        url: createUrl(ApiPath.Tests, testId, ApiPath.Files, id),
      }),
    }),
    deleteFile: builder.mutation<any, number>({
      query: (id) => ({
        data: {},
        method: 'DELETE',
        url: createUrl(ApiPath.Files, id),
      }),
    }),
    getBackgrounds: builder.query<
      GetManyFilesResponsePrivate,
      Partial<GetManyFilesQueryPrivate> & { id: number | string; isModal?: boolean }
    >({
      query: ({ id, isModal, ...params }) => ({
        method: 'GET',
        params,
        url: createUrl(ApiPath.Tests, id, ApiPath.Files),
      }),
    }),
    getFileById: builder.mutation<FileResponsePrivate, number>({
      query: (id) => ({
        method: 'GET',
        params: {
          relations: ['variants'],
        },
        url: createUrl(ApiPath.Files, id),
      }),
    }),
    uploadFile: builder.mutation<unknown, UploadFileProps>({
      query: ({ contentType, data, onUploadProgress, url }) => ({
        contentType,
        data,
        headers: { 'x-amz-acl': 'public-read' },
        method: 'PUT',
        onUploadProgress,
        url,
        withAuth: false,
        withCredentials: false,
        ignoreErrors: [400],
      }),
    }),
  }),
  reducerPath: 'filesApi',
});

export const {
  useCreateUploadFileMutation,
  useDeleteBackgroundMutation,
  useDeleteFileMutation,
  useGetBackgroundsQuery,
  useGetFileByIdMutation,
  useUploadFileMutation,
} = filesApi;
